import React, { useEffect, useState, memo } from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { Formik } from "formik";
import "./CreditorApproval.scss";
import Common from "us.common";
import { DrawerInfo, CreditorApprovalProps } from "./Interfaces";
import { DrawerTypes } from "./Constants";
import * as Actions from "us.collection.admin/actions";
import { useHistory } from "react-router-dom";
import { handleGoBack } from "us.helper/utility";
import { CreditorApprovalInfoTable, NewCreditorApproval } from "./Components";
import { CreditorApprovalInfo } from "us.collection.admin/interfaces";
import { RootState } from "us.helper/types";

const { $PageHeader, $Divider, $Skeleton, $Affix, $Drawer } = Common.Components;

/**
 * @description - Creditor Approval info home view component to manage creditor approvals
 * @link Design Document - https://unicorn-solutions.atlassian.net/wiki/spaces/USU/pages/4198006795/CreditorApproval+-+UI+Implementation+-+Fair
 * @author Keshan Nimnaka <keshana@unicorn-solutions.com>
 * @since 22/09/2025
 */

const Home: React.FC<PropsFromRedux & CreditorApprovalProps> = memo((props) => {
  const { t } = useTranslation(["US.COLLECTION.ADMIN", "US.COLLECTION.COMMON"]);
  const {
    getCreditorApprovalList,
    pendingApprovalsList,
    pendingApprovalsListLoading,
  } = props;

  const history = useHistory();

  const [drawerInfo, setDrawerInfo] = useState<DrawerInfo>({
    visibility: false,
    drawerType: "",
    record: {},
  });

  useEffect(() => {
    getCreditorApprovalList && getCreditorApprovalList({});
  }, []);

  /**
   * @description - Close the drawer
   */
  const closeDrawer = () => {
    setDrawerInfo({
      visibility: false,
      drawerType: "",
    });
  };

  /**
   * @description handle creditor approval item menu actions
   * @param record - selected credior  for action
   */
  const onHandleCreditorApprovalAction = (record: CreditorApprovalInfo) => {
    setDrawerInfo({
      visibility: true,
      drawerType: DrawerTypes.CREDITOR_APPROVAL_ADD,
      record,
    });
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{ creditorNumber: -1 }}
      onSubmit={(values: any, actions: any) => {}}
    >
      {({
        values,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        isValidating,
        resetForm,
        ...rest
      }: any) => (
        <>
          <div className="space-content manage-creditor-approval">
            <$Affix offsetTop={48}>
              <div className="page-header header-border pr-0">
                <div className="d-flex flex-row align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <$PageHeader
                      className="px-0"
                      onBack={() => handleGoBack(history)}
                      title={t(
                        "US.COLLECTION.ADMIN:CREDITOR_APPROVAL.MANAGE_CREDITOR_APPROVAL_INFO"
                      )}
                    />
                    <$Divider className="bui-devider" type="vertical" />
                  </div>
                </div>
              </div>
            </$Affix>
            <$Skeleton
              loading={pendingApprovalsListLoading}
              active
              paragraph={{ rows: 2 }}
            >
              <div>
                <CreditorApprovalInfoTable
                  tableData={pendingApprovalsList}
                  onCallAction={onHandleCreditorApprovalAction}
                />
              </div>
            </$Skeleton>
          </div>
          <$Drawer
            title={t(
              "US.COLLECTION.ADMIN:CREDITOR_APPROVAL.MANAGE_CREDITOR_APPROVAL_INFO"
            )}
            width={1150}
            visible={drawerInfo?.visibility}
            onClose={() =>
              setDrawerInfo({
                visibility: false,
                drawerType: "",
              })
            }
            destroyOnClose
          >
            {drawerInfo?.drawerType == DrawerTypes.CREDITOR_APPROVAL_ADD && (
              <NewCreditorApproval
                onClose={closeDrawer}
                drawerData={drawerInfo}
                isLoading={false}
              />
            )}
          </$Drawer>
        </>
      )}
    </Formik>
  );
});

const mapStateToProps = (state: RootState) => {
  const { creditorApprovalInfo } = state;
  const { pendingApprovalsList, pendingApprovalsListLoading } =
    creditorApprovalInfo;

  return {
    pendingApprovalsList,
    pendingApprovalsListLoading,
  };
};

const { creditorApprovalInfo } = Actions;
const { creditorApprovalList } = creditorApprovalInfo;

const mapDispatchToProps = {
  getCreditorApprovalList: creditorApprovalList.get,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(Home);
